import { Label } from '@ui/Label';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useUser } from '@client/hooks/use-user.hooks';
import { isAdmin } from '@shared/helpers/user.helpers';
import { tryCatchAwaitable } from '@shared/helpers/utils.helpers';
import type {
  AiModelSource,
  ResolvedAiModelUseCase,
} from '@shared/models/ai/ai-models.model';
import { getOpenAiReasoningSupport } from '@shared/models/ai/open-ai-reasoning.model';
import { adminAiModelsApi } from './admin-ai-models.api';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    overflow: 'auto',
    height: '100%',
  },
  model: {
    fontFamily: 'monospace',
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  env: {
    fontFamily: 'monospace',
    fontSize: 12,
  },
}));

const SOURCE_META: Record<
  AiModelSource,
  { label: string; color: 'default' | 'success' | 'warning'; tooltip: string }
> = {
  default: {
    label: 'Default',
    color: 'default',
    tooltip: 'No env override set — the default from source code is used.',
  },
  env: {
    label: 'Env override',
    color: 'success',
    tooltip: 'A valid env override is set and in effect.',
  },
  'env-ignored': {
    label: 'Env override ignored',
    color: 'warning',
    tooltip:
      'An env override is set but not in the allowlist, so it is ignored and the default is used.',
  },
};

/**
 * Reasoning effort cell: the configured effort in effect, the configured one
 * struck through when the active model rejects it, or the model's provider
 * default. Only OpenAI text use cases take `reasoning.effort`.
 */
const ReasoningEffortCell: React.FC<{
  useCase: ResolvedAiModelUseCase;
  className: string;
}> = ({ useCase, className }) => {
  if (useCase.provider !== 'openai' || useCase.modality !== 'text') {
    return <>—</>;
  }
  const configured = useCase.reasoningEffort;
  const active = useCase.resolvedReasoningEffort;
  if (configured !== undefined && active !== undefined) {
    return (
      <>
        {configured !== active && (
          <Tooltip title="The active model does not accept the configured effort, so its default is used.">
            <Box
              component="span"
              sx={{ textDecoration: 'line-through', mr: 1 }}
            >
              {configured}
            </Box>
          </Tooltip>
        )}
        <Box component="span" className={className}>
          {active}
        </Box>
      </>
    );
  }
  const providerDefault = getOpenAiReasoningSupport(
    useCase.resolvedModel,
  )?.defaultEffort;
  return (
    <Box component="span" sx={{ color: 'text.secondary' }}>
      {providerDefault ? `${providerDefault} (provider default)` : '—'}
    </Box>
  );
};

export const AdminAIModels: React.FC = () => {
  const { classes } = useStyles();
  const user = useUser();
  const disabled = useMemo(() => !user || !isAdmin(user), [user]);

  const [useCases, setUseCases] = useState<ResolvedAiModelUseCase[]>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (disabled) return;
    void (async () => {
      const res = await tryCatchAwaitable(adminAiModelsApi.list());
      if (res.ok) {
        setUseCases(res.value);
      } else {
        setError('Failed to load active models.');
      }
    })();
  }, [disabled]);

  if (disabled) {
    return (
      <Box
        sx={{
          p: 4,
        }}
      >
        <Typography variant="h6">Not allowed</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body1">
          The model actually in effect for each AI use case. These values are{' '}
          <strong>per environment</strong> — this page shows the one you are
          connected to now (e.g. app.nodon.se for prod).
        </Typography>
        <Typography
          component="ul"
          variant="body1"
          sx={{
            mt: 1,
            mb: 0,
            pl: 3,
            '& li': { mb: 1, '&:last-of-type': { mb: 0 } },
          }}
        >
          <li>
            <strong>Default</strong> — no env override; the default from source
            code is used.
          </li>
          <li>
            <strong>Env override</strong> — a valid <code>OPEN_AI_MODEL</code> /{' '}
            <code>GEMINI_MODEL</code> is set and in effect.
          </li>
          <li>
            <strong>Env override ignored</strong> — an override is set but not
            in the allowlist, so it is ignored and the default is used.
          </li>
          <li>
            <strong>Reasoning</strong> — the <code>reasoning.effort</code> sent
            to OpenAI. A value set in source code is highlighted like the active
            model; it is struck through when the active model does not accept
            it. Otherwise the provider default for the model applies, shown in
            grey.
          </li>
          <li>
            <strong>UI-selectable</strong> — the model can be picked per request
            in the UI (usually only admins), so the active model is just the
            fallback when nothing is picked.
          </li>
        </Typography>
      </Paper>

      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}>
            <TableRow>
              <TableCell>Use case</TableCell>
              <TableCell>Active model</TableCell>
              <TableCell>Reasoning</TableCell>
              <TableCell>Default (Source code)</TableCell>
              <TableCell>Env override</TableCell>
              <TableCell>UI-selectable</TableCell>
              <TableCell sx={{ pl: 3 }}>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(useCases ?? []).map((useCase) => {
              const source = SOURCE_META[useCase.source];
              return (
                <TableRow key={useCase.id} hover>
                  <TableCell>{useCase.label}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={source.tooltip}>
                      <Label
                        label={source.label}
                        color={source.color}
                        sx={{ mr: 1 }}
                      />
                    </Tooltip>
                    <Box component="span" className={classes.model}>
                      {useCase.resolvedModel}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <ReasoningEffortCell
                      useCase={useCase}
                      className={classes.model}
                    />
                  </TableCell>
                  <TableCell className={classes.model}>
                    {useCase.defaultModel}
                  </TableCell>
                  <TableCell
                    className={classes.env}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {useCase.envOverride ? (
                      <>
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {useCase.envOverride}
                        </Box>
                        {': '}
                        {useCase.envValue ? (
                          <Box
                            component="span"
                            sx={{
                              textDecoration:
                                useCase.source === 'env-ignored'
                                  ? 'line-through'
                                  : 'none',
                            }}
                          >
                            {useCase.envValue}
                          </Box>
                        ) : (
                          <Box
                            component="span"
                            sx={{ color: 'text.secondary' }}
                          >
                            unset
                          </Box>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{useCase.uiSelectable ? 'Yes' : '—'}</TableCell>
                  <TableCell sx={{ pl: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {useCase.description}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
